// Simple translation utility for MATE Korean Learning Profile Survey
// Supports English, Indonesian, Korean, and Vietnamese languages

export type Language = 'en' | 'id' | 'ko' | 'vn';

// Dictionary of translations using English sentences as keys
export const translations: Record<string, Record<Language, string>> = {
    // Header
    "Korean Learning Profile Survey": {
        en: "Korean Learning Profile Survey",
        id: "Survei Profil Belajar Bahasa Korea",
        ko: "한국어 학습 프로필 설문",
        vn: "Khảo Sát Hồ Sơ Học Tiếng Hàn"
    },
    "Talk and Learn Korean": {
        en: "Talk and Learn Korean",
        id: "Talk and Learn Korean",
        ko: "대화하며 한국어 배우기",
        vn: "Nói Chuyện và Học Tiếng Hàn"
    },
    "Your responses will help us tailor our Korean language sessions to your specific needs and learning goals. All information provided will be kept confidential and used only for educational purposes.": {
        en: "Your responses will help us tailor our Korean language sessions to your specific needs and learning goals. All information provided will be kept confidential and used only for educational purposes.",
        id: "Jawaban Anda akan membantu kami menyesuaikan sesi belajar bahasa Korea dengan kebutuhan dan tujuan belajar Anda. Semua informasi yang diberikan akan dijaga kerahasiaannya dan hanya digunakan untuk tujuan pendidikan.",
        ko: "귀하의 응답은 특정 요구사항과 학습 목표에 맞춰 한국어 세션을 맞춤화하는 데 도움이 됩니다. 제공된 모든 정보는 기밀로 유지되며 교육 목적으로만 사용됩니다.",
        vn: "Câu trả lời của bạn sẽ giúp chúng tôi điều chỉnh các buổi học tiếng Hàn theo nhu cầu và mục tiêu học tập cụ thể của bạn. Tất cả thông tin được cung cấp sẽ được giữ bí mật và chỉ sử dụng cho mục đích giáo dục."
    },
    
    // Personal Information
    "Personal Information": {
        en: "Personal Information",
        id: "Personal Information",
        ko: "개인 정보",
        vn: "Thông Tin Cá Nhân"
    },
    "Full Name": {
        en: "Full Name",
        id: "Nama Lengkap",
        ko: "이름",
        vn: "Họ và Tên"
    },
    "Email Address": {
        en: "Email Address",
        id: "Alamat Email",
        ko: "이메일 주소",
        vn: "Địa Chỉ Email"
    },
    "Phone Number": {
        en: "Phone Number",
        id: "Nomor Telepon",
        ko: "전화번호",
        vn: "Số Điện Thoại"
    },
    "Age": {
        en: "Age",
        id: "Usia",
        ko: "나이",
        vn: "Tuổi"
    },
    "Select your age range": {
        en: "Select your age range",
        id: "Pilih rentang usia Anda",
        ko: "연령대를 선택하세요",
        vn: "Chọn độ tuổi của bạn"
    },
    "Under 18": {
        en: "Under 18",
        id: "Di bawah 18",
        ko: "18세 미만",
        vn: "Dưới 18"
    },
    "18-24": {
        en: "18-24",
        id: "18-24",
        ko: "18-24",
        vn: "18-24"
    },
    "25-34": {
        en: "25-34",
        id: "25-34",
        ko: "25-34",
        vn: "25-34"
    },
    "35-44": {
        en: "35-44",
        id: "35-44",
        ko: "35-44",
        vn: "35-44"
    },
    "45-54": {
        en: "45-54",
        id: "45-54",
        ko: "45-54",
        vn: "45-54"
    },
    "55+": {
        en: "55+",
        id: "55+",
        ko: "55+",
        vn: "55+"
    },
    "Country of Residence": {
        en: "Country of Residence",
        id: "Negara Tempat Tinggal",
        ko: "거주 국가",
        vn: "Quốc Gia Cư Trú"
    },
    "Your Timezone": {
        en: "Your Timezone",
        id: "Zona Waktu Anda",
        ko: "시간대",
        vn: "Múi Giờ Của Bạn"
    },
    "e.g. GMT+8, EST, etc.": {
        en: "e.g. GMT+8, EST, etc.",
        id: "contoh: GMT+7, WIB, dll.",
        ko: "예: GMT+9, KST 등",
        vn: "ví dụ: GMT+7, ICT, v.v."
    },
    
    // Language Background
    "Language Background": {
        en: "Language Background",
        id: "Language Background",
        ko: "언어 배경",
        vn: "Lý Lịch Ngôn Ngữ"
    },
    "Native Language": {
        en: "Native Language",
        id: "Bahasa Ibu",
        ko: "모국어",
        vn: "Ngôn Ngữ Mẹ Đẻ"
    },
    "Other Languages You Speak": {
        en: "Other Languages You Speak",
        id: "Bahasa Lain yang Anda Kuasai",
        ko: "구사 가능한 다른 언어",
        vn: "Các Ngôn Ngữ Khác Bạn Nói"
    },
    "List any other languages you speak and your proficiency level": {
        en: "List any other languages you speak and your proficiency level",
        id: "Sebutkan bahasa lain yang Anda kuasai dan tingkat kemahiran Anda",
        ko: "구사하는 다른 언어와 숙련도를 나열하세요",
        vn: "Liệt kê các ngôn ngữ khác bạn nói và trình độ của bạn"
    },
    "Current Korean Language Level": {
        en: "Current Korean Language Level",
        id: "Tingkat Bahasa Korea Saat Ini",
        ko: "현재 한국어 수준",
        vn: "Trình Độ Tiếng Hàn Hiện Tại"
    },
    "Select your level": {
        en: "Select your level",
        id: "Pilih tingkat Anda",
        ko: "수준을 선택하세요",
        vn: "Chọn trình độ của bạn"
    },
    "Absolute Beginner (No knowledge)": {
        en: "Absolute Beginner (No knowledge)",
        id: "Pemula Absolut (Tidak memiliki pengetahuan)",
        ko: "완전 초보자 (지식 없음)",
        vn: "Người Mới Bắt Đầu Hoàn Toàn (Không có kiến thức)"
    },
    "Beginner (Know Hangul, basic greetings)": {
        en: "Beginner (Know Hangul, basic greetings)",
        id: "Pemula (Mengenal Hangul, salam dasar)",
        ko: "초급 (한글, 기본 인사말 알고 있음)",
        vn: "Người Mới Bắt Đầu (Biết Hangul, lời chào cơ bản)"
    },
    "Elementary (Simple conversations, basic grammar)": {
        en: "Elementary (Simple conversations, basic grammar)",
        id: "Dasar (Percakapan sederhana, tata bahasa dasar)",
        ko: "초급 (간단한 대화, 기본 문법)",
        vn: "Sơ Cấp (Hội thoại đơn giản, ngữ pháp cơ bản)"
    },
    "Intermediate (Can discuss familiar topics)": {
        en: "Intermediate (Can discuss familiar topics)",
        id: "Menengah (Dapat mendiskusikan topik yang familiar)",
        ko: "중급 (익숙한 주제 토론 가능)",
        vn: "Trung Cấp (Có thể thảo luận các chủ đề quen thuộc)"
    },
    "Advanced (Fluent in most situations)": {
        en: "Advanced (Fluent in most situations)",
        id: "Lanjutan (Lancar dalam kebanyakan situasi)",
        ko: "고급 (대부분의 상황에서 유창함)",
        vn: "Nâng Cao (Lưu loát trong hầu hết các tình huống)"
    },
    "Native/Bilingual": {
        en: "Native/Bilingual",
        id: "Penutur Asli/Bilingual",
        ko: "원어민/이중언어",
        vn: "Người Bản Ngữ/Song Ngữ"
    },
    "Have you studied Korean before?": {
        en: "Have you studied Korean before?",
        id: "Apakah Anda pernah belajar bahasa Korea sebelumnya?",
        ko: "한국어를 배운 적이 있나요?",
        vn: "Bạn đã từng học tiếng Hàn trước đây chưa?"
    },
    "Yes": {
        en: "Yes",
        id: "Ya",
        ko: "예",
        vn: "Có"
    },
    "No": {
        en: "No",
        id: "Tidak",
        ko: "아니오",
        vn: "Không"
    },
    "If yes, please describe your previous Korean learning experience:": {
        en: "If yes, please describe your previous Korean learning experience:",
        id: "Jika ya, jelaskan pengalaman belajar bahasa Korea Anda sebelumnya:",
        ko: "그렇다면 이전 한국어 학습 경험을 설명해주세요:",
        vn: "Nếu có, vui lòng mô tả kinh nghiệm học tiếng Hàn trước đây của bạn:"
    },
    "Describe any courses, self-study, tutoring, etc.": {
        en: "Describe any courses, self-study, tutoring, etc.",
        id: "Jelaskan kursus, belajar mandiri, bimbingan, dll.",
        ko: "코스, 독학, 과외 등을 설명해주세요",
        vn: "Mô tả các khóa học, tự học, gia sư, v.v."
    },
    
    // Learning Goals
    "Learning Goals & Motivation": {
        en: "Learning Goals & Motivation",
        id: "Learning Goals & Motivation",
        ko: "학습 목표 및 동기",
        vn: "Mục Tiêu và Động Lực Học Tập"
    },
    "Why do you want to learn Korean? (Select all that apply)": {
        en: "Why do you want to learn Korean? (Select all that apply)",
        id: "Mengapa Anda ingin belajar bahasa Korea? (Pilih semua yang sesuai)",
        ko: "한국어를 배우고 싶은 이유는? (해당하는 모든 항목 선택)",
        vn: "Tại sao bạn muốn học tiếng Hàn? (Chọn tất cả các mục phù hợp)"
    },
    "Travel to Korea": {
        en: "Travel to Korea",
        id: "Bepergian ke Korea",
        ko: "한국 여행",
        vn: "Du Lịch Hàn Quốc"
    },
    "K-pop/K-drama interest": {
        en: "K-pop/K-drama interest",
        id: "Minat K-pop/K-drama",
        ko: "K-pop/K-drama 관심",
        vn: "Quan Tâm K-pop/K-drama"
    },
    "Business/Professional": {
        en: "Business/Professional",
        id: "Bisnis/Profesional",
        ko: "비즈니스/전문직",
        vn: "Kinh Doanh/Chuyên Nghiệp"
    },
    "Academic purposes": {
        en: "Academic purposes",
        id: "Tujuan akademis",
        ko: "학업 목적",
        vn: "Mục Đích Học Thuật"
    },
    "Korean family/heritage": {
        en: "Korean family/heritage",
        id: "Keluarga/warisan Korea",
        ko: "한국 가족/유산",
        vn: "Gia Đình/Di Sản Hàn Quốc"
    },
    "Korean friends/relationship": {
        en: "Korean friends/relationship",
        id: "Teman/hubungan dengan orang Korea",
        ko: "한국 친구/관계",
        vn: "Bạn Bè/Mối Quan Hệ Hàn Quốc"
    },
    "Other": {
        en: "Other",
        id: "Lainnya",
        ko: "기타",
        vn: "Khác"
    },
    "What specific skills would you like to focus on?": {
        en: "What specific skills would you like to focus on?",
        id: "Keterampilan spesifik apa yang ingin Anda fokuskan?",
        ko: "어떤 구체적인 기술에 집중하고 싶나요?",
        vn: "Bạn muốn tập trung vào kỹ năng cụ thể nào?"
    },
    "Speaking/Conversation": {
        en: "Speaking/Conversation",
        id: "Berbicara/Percakapan",
        ko: "말하기/대화",
        vn: "Nói/Hội Thoại"
    },
    "Listening Comprehension": {
        en: "Listening Comprehension",
        id: "Pemahaman Mendengarkan",
        ko: "듣기 이해",
        vn: "Nghe Hiểu"
    },
    "Reading": {
        en: "Reading",
        id: "Membaca",
        ko: "읽기",
        vn: "Đọc"
    },
    "Writing": {
        en: "Writing",
        id: "Menulis",
        ko: "쓰기",
        vn: "Viết"
    },
    "Grammar": {
        en: "Grammar",
        id: "Tata Bahasa",
        ko: "문법",
        vn: "Ngữ Pháp"
    },
    "Vocabulary Building": {
        en: "Vocabulary Building",
        id: "Membangun Kosakata",
        ko: "어휘 확장",
        vn: "Xây Dựng Từ Vựng"
    },
    "Cultural Understanding": {
        en: "Cultural Understanding",
        id: "Pemahaman Budaya",
        ko: "문화 이해",
        vn: "Hiểu Biết Văn Hóa"
    },
    "What is your timeframe for achieving your Korean language goals?": {
        en: "What is your timeframe for achieving your Korean language goals?",
        id: "Berapa jangka waktu Anda untuk mencapai tujuan belajar bahasa Korea?",
        ko: "한국어 목표를 달성하기 위한 기간은 얼마나 되나요?",
        vn: "Khung thời gian của bạn để đạt được mục tiêu tiếng Hàn là gì?"
    },
    "Select a timeframe": {
        en: "Select a timeframe",
        id: "Pilih jangka waktu",
        ko: "기간을 선택하세요",
        vn: "Chọn khung thời gian"
    },
    "1-3 months": {
        en: "1-3 months",
        id: "1-3 bulan",
        ko: "1-3개월",
        vn: "1-3 tháng"
    },
    "3-6 months": {
        en: "3-6 months",
        id: "3-6 bulan",
        ko: "3-6개월",
        vn: "3-6 tháng"
    },
    "6-12 months": {
        en: "6-12 months",
        id: "6-12 bulan",
        ko: "6-12개월",
        vn: "6-12 tháng"
    },
    "1-2 years": {
        en: "1-2 years",
        id: "1-2 tahun",
        ko: "1-2년",
        vn: "1-2 năm"
    },
    "Long-term learning (2+ years)": {
        en: "Long-term learning (2+ years)",
        id: "Pembelajaran jangka panjang (2+ tahun)",
        ko: "장기 학습 (2년 이상)",
        vn: "Học Dài Hạn (2+ năm)"
    },
    "What motivates you to continue learning even when it gets difficult?": {
        en: "What motivates you to continue learning even when it gets difficult?",
        id: "Apa yang memotivasi Anda untuk terus belajar bahkan ketika menjadi sulit?",
        ko: "어려워져도 계속 학습할 수 있게 하는 동기는 무엇인가요?",
        vn: "Điều gì thúc đẩy bạn tiếp tục học ngay cả khi khó khăn?"
    },
    
    // Learning Preferences
    "Learning Preferences": {
        en: "Learning Preferences",
        id: "Learning Preferences",
        ko: "학습 선호도",
        vn: "Sở Thích Học Tập"
    },
    "How would you describe your learning style?": {
        en: "How would you describe your learning style?",
        id: "Bagaimana Anda menggambarkan gaya belajar Anda?",
        ko: "학습 스타일을 어떻게 설명하시겠습니까?",
        vn: "Bạn mô tả phong cách học tập của mình như thế nào?"
    },
    "Visual (learning through images, videos, diagrams)": {
        en: "Visual (learning through images, videos, diagrams)",
        id: "Visual (belajar melalui gambar, video, diagram)",
        ko: "시각적 (이미지, 비디오, 다이어그램을 통한 학습)",
        vn: "Hình Ảnh (học qua hình ảnh, video, sơ đồ)"
    },
    "Auditory (learning through listening and speaking)": {
        en: "Auditory (learning through listening and speaking)",
        id: "Auditori (belajar melalui mendengarkan dan berbicara)",
        ko: "청각적 (듣기와 말하기를 통한 학습)",
        vn: "Thính Giác (học qua nghe và nói)"
    },
    "Reading/Writing (learning through text and notes)": {
        en: "Reading/Writing (learning through text and notes)",
        id: "Membaca/Menulis (belajar melalui teks dan catatan)",
        ko: "읽기/쓰기 (텍스트와 노트를 통한 학습)",
        vn: "Đọc/Viết (học qua văn bản và ghi chú)"
    },
    "Kinesthetic (learning through practice and experience)": {
        en: "Kinesthetic (learning through practice and experience)",
        id: "Kinestetik (belajar melalui praktik dan pengalaman)",
        ko: "운동감각적 (실습과 경험을 통한 학습)",
        vn: "Vận Động (học qua thực hành và trải nghiệm)"
    },
    "How much time can you dedicate to studying Korean per week?": {
        en: "How much time can you dedicate to studying Korean per week?",
        id: "Berapa banyak waktu yang dapat Anda dedikasikan untuk belajar bahasa Korea per minggu?",
        ko: "주당 한국어 학습에 얼마나 많은 시간을 할애할 수 있나요?",
        vn: "Bạn có thể dành bao nhiêu thời gian để học tiếng Hàn mỗi tuần?"
    },
    "Select time commitment": {
        en: "Select time commitment",
        id: "Pilih komitmen waktu",
        ko: "시간 약속 선택",
        vn: "Chọn cam kết thời gian"
    },
    "Less than 1 hour": {
        en: "Less than 1 hour",
        id: "Kurang dari 1 jam",
        ko: "1시간 미만",
        vn: "Dưới 1 giờ"
    },
    "1-3 hours": {
        en: "1-3 hours",
        id: "1-3 jam",
        ko: "1-3시간",
        vn: "1-3 giờ"
    },
    "4-7 hours": {
        en: "4-7 hours",
        id: "4-7 jam",
        ko: "4-7시간",
        vn: "4-7 giờ"
    },
    "8+ hours": {
        en: "8+ hours",
        id: "8+ jam",
        ko: "8시간 이상",
        vn: "8+ giờ"
    },
    "Preferred session frequency:": {
        en: "Preferred session frequency:",
        id: "Frekuensi sesi yang disukai:",
        ko: "선호하는 세션 빈도:",
        vn: "Tần suất buổi học ưa thích:"
    },
    "Select frequency": {
        en: "Select frequency",
        id: "Pilih frekuensi",
        ko: "빈도 선택",
        vn: "Chọn tần suất"
    },
    "Once a week": {
        en: "Once a week",
        id: "Sekali seminggu",
        ko: "주 1회",
        vn: "Một lần mỗi tuần"
    },
    "Twice a week": {
        en: "Twice a week",
        id: "Dua kali seminggu",
        ko: "주 2회",
        vn: "Hai lần mỗi tuần"
    },
    "Three times a week": {
        en: "Three times a week",
        id: "Tiga kali seminggu",
        ko: "주 3회",
        vn: "Ba lần mỗi tuần"
    },
    "Daily": {
        en: "Daily",
        id: "Setiap hari",
        ko: "매일",
        vn: "Hàng ngày"
    },
    "Flexible/As needed": {
        en: "Flexible/As needed",
        id: "Fleksibel/Sesuai kebutuhan",
        ko: "유연함/필요에 따라",
        vn: "Linh hoạt/Theo nhu cầu"
    },
    "Preferred learning approach:": {
        en: "Preferred learning approach:",
        id: "Pendekatan belajar yang disukai:",
        ko: "선호하는 학습 접근법:",
        vn: "Phương pháp học ưa thích:"
    },
    "Structured (following a curriculum, textbook-based)": {
        en: "Structured (following a curriculum, textbook-based)",
        id: "Terstruktur (mengikuti kurikulum, berbasis buku teks)",
        ko: "체계적 (커리큘럼, 교재 기반)",
        vn: "Có Cấu Trúc (theo chương trình, dựa trên sách giáo khoa)"
    },
    "Conversational (learning through natural dialogue)": {
        en: "Conversational (learning through natural dialogue)",
        id: "Percakapan (belajar melalui dialog alami)",
        ko: "대화형 (자연스러운 대화를 통한 학습)",
        vn: "Đàm Thoại (học qua hội thoại tự nhiên)"
    },
    "Mixed approach": {
        en: "Mixed approach",
        id: "Pendekatan campuran",
        ko: "혼합 접근법",
        vn: "Phương pháp hỗn hợp"
    },
    
    // Cultural Interests
    "Cultural Interests": {
        en: "Cultural Interests",
        id: "Cultural Interests",
        ko: "문화적 관심사",
        vn: "Sở Thích Văn Hóa"
    },
    "Which aspects of Korean culture interest you? (Select all that apply)": {
        en: "Which aspects of Korean culture interest you? (Select all that apply)",
        id: "Aspek budaya Korea mana yang menarik minat Anda? (Pilih semua yang sesuai)",
        ko: "한국 문화의 어떤 측면에 관심이 있나요? (해당하는 모든 항목 선택)",
        vn: "Những khía cạnh văn hóa Hàn Quốc nào khiến bạn quan tâm? (Chọn tất cả các mục phù hợp)"
    },
    "K-pop": {
        en: "K-pop",
        id: "K-pop",
        ko: "K-pop",
        vn: "K-pop"
    },
    "K-dramas": {
        en: "K-dramas",
        id: "K-drama",
        ko: "K-드라마",
        vn: "K-drama"
    },
    "Korean Cuisine": {
        en: "Korean Cuisine",
        id: "Masakan Korea",
        ko: "한국 요리",
        vn: "Ẩm Thực Hàn Quốc"
    },
    "History": {
        en: "History",
        id: "Sejarah",
        ko: "역사",
        vn: "Lịch Sử"
    },
    "Literature": {
        en: "Literature",
        id: "Sastra",
        ko: "문학",
        vn: "Văn Học"
    },
    "Business Culture": {
        en: "Business Culture",
        id: "Budaya Bisnis",
        ko: "비즈니스 문화",
        vn: "Văn Hóa Kinh Doanh"
    },
    "Traditional Culture": {
        en: "Traditional Culture",
        id: "Budaya Tradisional",
        ko: "전통 문화",
        vn: "Văn Hóa Truyền Thống"
    },
    "Modern Society": {
        en: "Modern Society",
        id: "Masyarakat Modern",
        ko: "현대 사회",
        vn: "Xã Hội Hiện Đại"
    },
    "Do you have any favorite Korean media (shows, music, films) that you'd like to incorporate into your learning?": {
        en: "Do you have any favorite Korean media (shows, music, films) that you'd like to incorporate into your learning?",
        id: "Apakah Anda memiliki media Korea favorit (acara, musik, film) yang ingin Anda masukkan ke dalam pembelajaran Anda?",
        ko: "학습에 통합하고 싶은 좋아하는 한국 미디어(쇼, 음악, 영화)가 있나요?",
        vn: "Bạn có phương tiện truyền thông Hàn Quốc yêu thích nào (chương trình, âm nhạc, phim) muốn đưa vào việc học không?"
    },
    
    // Additional Information
    "Additional Information": {
        en: "Additional Information",
        id: "Additional Information",
        ko: "추가 정보",
        vn: "Thông Tin Bổ Sung"
    },
    "Do you have any concerns or challenges with language learning that we should be aware of?": {
        en: "Do you have any concerns or challenges with language learning that we should be aware of?",
        id: "Apakah Anda memiliki kekhawatiran atau tantangan dalam belajar bahasa yang perlu kami ketahui?",
        ko: "저희가 알아야 할 언어 학습에 대한 우려나 어려움이 있나요?",
        vn: "Bạn có bất kỳ lo ngại hoặc thách thức nào về việc học ngôn ngữ mà chúng tôi cần biết không?"
    },
    "What are your expectations for these Korean language sessions?": {
        en: "What are your expectations for these Korean language sessions?",
        id: "Apa harapan Anda untuk sesi belajar bahasa Korea ini?",
        ko: "이 한국어 세션에 대한 기대는 무엇인가요?",
        vn: "Kỳ vọng của bạn về các buổi học tiếng Hàn này là gì?"
    },
    "Any other information you'd like to share with your tutor?": {
        en: "Any other information you'd like to share with your tutor?",
        id: "Informasi lain yang ingin Anda bagikan dengan tutor Anda?",
        ko: "튜터와 공유하고 싶은 다른 정보가 있나요?",
        vn: "Có thông tin nào khác bạn muốn chia sẻ với gia sư không?"
    },
    
    // Korean Learning Options
    "Choose Your Korean Learning Option": {
        en: "Choose Your Korean Learning Option",
        id: "Pilih Opsi Pembelajaran Bahasa Korea Anda",
        ko: "한국어 학습 옵션 선택",
        vn: "Chọn Tùy Chọn Học Tiếng Hàn"
    },
    "One-time 1-on-1 Session": {
        en: "One-time 1-on-1 Session",
        id: "Sesi 1-on-1 Satu Kali",
        ko: "일회성 1:1 세션",
        vn: "Buổi Học 1-1 Một Lần"
    },
    "Ask for price": {
        en: "Ask for price",
        id: "Tanyakan harga",
        ko: "가격 문의",
        vn: "Hỏi giá"
    },
    "Individual Korean language session with a tutor": {
        en: "Individual Korean language session with a tutor",
        id: "Sesi bahasa Korea individual dengan tutor",
        ko: "튜터와 함께하는 개인 한국어 세션",
        vn: "Buổi học tiếng Hàn cá nhân với gia sư"
    },
    "Recommended for:": {
        en: "Recommended for:",
        id: "Direkomendasikan untuk:",
        ko: "추천 대상:",
        vn: "Được khuyến nghị cho:"
    },
    "Those preparing travel in Korea": {
        en: "Those preparing travel in Korea",
        id: "Mereka yang mempersiapkan perjalanan ke Korea",
        ko: "한국 여행을 준비하는 분들",
        vn: "Những người chuẩn bị du lịch Hàn Quốc"
    },
    "Example: Discuss your 3-night/4-day Korean tour plan and get recommendations for buying cosmetics at Olive Young in a 30-minute session.": {
        en: "Example: Discuss your 3-night/4-day Korean tour plan and get recommendations for buying cosmetics at Olive Young in a 30-minute session.",
        id: "Contoh: Diskusikan rencana tur Korea 3-malam/4-hari Anda dan dapatkan rekomendasi untuk membeli kosmetik di Olive Young dalam sesi 30 menit.",
        ko: "예시: 3박 4일 한국 여행 계획을 논의하고 30분 세션에서 올리브영에서 화장품 구매에 대한 추천을 받으세요.",
        vn: "Ví dụ: Thảo luận kế hoạch tour Hàn Quốc 3 đêm/4 ngày và nhận đề xuất mua mỹ phẩm tại Olive Young trong buổi học 30 phút."
    },
    "Prep Session for Multiple 1-on-1": {
        en: "Prep Session for Multiple 1-on-1",
        id: "Sesi Persiapan untuk Beberapa Sesi 1-on-1",
        ko: "다중 1:1을 위한 준비 세션",
        vn: "Buổi Chuẩn Bị cho Nhiều Buổi 1-1"
    },
    "Preparation sessions to maximize your learning": {
        en: "Preparation sessions to maximize your learning",
        id: "Sesi persiapan untuk memaksimalkan pembelajaran Anda",
        ko: "학습을 극대화하기 위한 준비 세션",
        vn: "Các buổi chuẩn bị để tối đa hóa việc học của bạn"
    },
    "Those preparing TOPIK, study abroad, etc.": {
        en: "Those preparing TOPIK, study abroad, etc.",
        id: "Mereka yang mempersiapkan TOPIK, studi di luar negeri, dll.",
        ko: "TOPIK, 유학 등을 준비하는 분들",
        vn: "Những người chuẩn bị TOPIK, du học, v.v."
    },
    "Example: We will examine your current level and plan 4-12 sessions to help you achieve your Korean language goals.": {
        en: "Example: We will examine your current level and plan 4-12 sessions to help you achieve your Korean language goals.",
        id: "Contoh: Kami akan mengevaluasi tingkat Anda saat ini dan merencanakan 4-12 sesi untuk membantu Anda mencapai tujuan bahasa Korea Anda.",
        ko: "예시: 현재 수준을 평가하고 한국어 목표 달성을 돕기 위해 4-12회의 세션을 계획합니다.",
        vn: "Ví dụ: Chúng tôi sẽ kiểm tra trình độ hiện tại của bạn và lên kế hoạch 4-12 buổi học để giúp bạn đạt được mục tiêu tiếng Hàn."
    },
    "4 Week In-a-row, 1-on-6 Session": {
        en: "4 Week In-a-row, 1-on-6 Session",
        id: "Sesi 1-on-6 4 Minggu Berturut-turut",
        ko: "4주 연속 1:6 세션",
        vn: "Buổi Học 1-6 Liên Tiếp 4 Tuần"
    },
    "Four consecutive weeks of group sessions": {
        en: "Four consecutive weeks of group sessions",
        id: "Empat minggu berturut-turut sesi kelompok",
        ko: "4주 연속 그룹 세션",
        vn: "Bốn tuần liên tiếp các buổi học nhóm"
    },
    "Consistent learners seeking group interaction": {
        en: "Consistent learners seeking group interaction",
        id: "Pembelajar konsisten yang mencari interaksi kelompok",
        ko: "그룹 상호작용을 원하는 꾸준한 학습자",
        vn: "Người học đều đặn tìm kiếm tương tác nhóm"
    },
    "Example: Join a group of Day6 fans for Korean song lessons and engaging conversations about Korean culture.": {
        en: "Example: Join a group of Day6 fans for Korean song lessons and engaging conversations about Korean culture.",
        id: "Contoh: Bergabunglah dengan grup penggemar Day6 untuk pelajaran lagu Korea dan percakapan menarik tentang budaya Korea.",
        ko: "예시: Day6 팬 그룹과 함께 한국 노래 수업과 한국 문화에 대한 흥미로운 대화를 나누세요.",
        vn: "Ví dụ: Tham gia nhóm fan Day6 để học bài hát tiếng Hàn và trò chuyện hấp dẫn về văn hóa Hàn Quốc."
    },
    
    // Button & Footer
    "Submit Survey": {
        en: "Submit Survey",
        id: "Submit Survey",
        ko: "설문 제출",
        vn: "Gửi Khảo Sát"
    },
    "Thank you for taking the time to complete this survey. Your responses will help us create a personalized learning experience for you.": {
        en: "Thank you for taking the time to complete this survey. Your responses will help us create a personalized learning experience for you.",
        id: "Terima kasih telah meluangkan waktu untuk mengisi survei ini. Jawaban Anda akan membantu kami menciptakan pengalaman belajar yang dipersonalisasi untuk Anda.",
        ko: "이 설문에 시간을 내어 주셔서 감사합니다. 귀하의 응답은 맞춤형 학습 경험을 만드는 데 도움이 됩니다.",
        vn: "Cảm ơn bạn đã dành thời gian hoàn thành khảo sát này. Câu trả lời của bạn sẽ giúp chúng tôi tạo ra trải nghiệm học tập được cá nhân hóa cho bạn."
    },
    "Thank you for submitting your profile! Your tutor will review this information before your first session.": {
        en: "Thank you for submitting your profile! Your tutor will review this information before your first session.",
        id: "Terima kasih telah mengirimkan profil Anda! Tutor Anda akan meninjau informasi ini sebelum sesi pertama Anda.",
        ko: "프로필을 제출해 주셔서 감사합니다! 튜터가 첫 세션 전에 이 정보를 검토할 것입니다.",
        vn: "Cảm ơn bạn đã gửi hồ sơ! Gia sư của bạn sẽ xem xét thông tin này trước buổi học đầu tiên."
    },
    
    // Survey Submitted Page
    "Survey Submitted Successfully!": {
        en: "Survey Submitted Successfully!",
        id: "Survei Berhasil Dikirim!",
        ko: "설문이 성공적으로 제출되었습니다!",
        vn: "Khảo Sát Đã Gửi Thành Công!"
    },
    "Thank you for submitting your Korean Learning Profile. Your tutor will review this information before your first session.": {
        en: "Thank you for submitting your Korean Learning Profile. Your tutor will review this information before your first session.",
        id: "Terima kasih telah mengirimkan Profil Belajar Bahasa Korea Anda. Tutor Anda akan meninjau informasi ini sebelum sesi pertama Anda.",
        ko: "한국어 학습 프로필을 제출해 주셔서 감사합니다. 튜터가 첫 세션 전에 이 정보를 검토할 것입니다.",
        vn: "Cảm ơn bạn đã gửi Hồ Sơ Học Tiếng Hàn. Gia sư của bạn sẽ xem xét thông tin này trước buổi học đầu tiên."
    },
    "You'll be contacted soon with next steps for your Korean language journey.": {
        en: "You'll be contacted soon with next steps for your Korean language journey.",
        id: "Anda akan segera dihubungi untuk langkah selanjutnya dalam perjalanan belajar bahasa Korea Anda.",
        ko: "곧 한국어 학습 여정의 다음 단계에 대해 연락 드리겠습니다.",
        vn: "Bạn sẽ sớm được liên lạc với các bước tiếp theo cho hành trình học tiếng Hàn của bạn."
    },
    "What's Next?": {
        en: "What's Next?",
        id: "Apa Selanjutnya?",
        ko: "다음 단계는?",
        vn: "Tiếp Theo Là Gì?"
    },
    "Your tutor will review your profile": {
        en: "Your tutor will review your profile",
        id: "Tutor Anda akan meninjau profil Anda",
        ko: "튜터가 프로필을 검토할 것입니다",
        vn: "Gia sư sẽ xem xét hồ sơ của bạn"
    },
    "You'll receive a confirmation email within 24-48 hours": {
        en: "You'll receive a confirmation email within 24-48 hours",
        id: "Anda akan menerima email konfirmasi dalam waktu 24-48 jam",
        ko: "24-48시간 내에 확인 이메일을 받으실 것입니다",
        vn: "Bạn sẽ nhận được email xác nhận trong vòng 24-48 giờ"
    },
    "We'll schedule your first session based on your preferences": {
        en: "We'll schedule your first session based on your preferences",
        id: "Kami akan menjadwalkan sesi pertama Anda berdasarkan preferensi Anda",
        ko: "선호도에 따라 첫 세션 일정을 잡겠습니다",
        vn: "Chúng tôi sẽ lên lịch buổi học đầu tiên dựa trên sở thích của bạn"
    },
    "If you have any questions, please contact us at:": {
        en: "If you have any questions, please contact us at:",
        id: "Jika Anda memiliki pertanyaan, silakan hubungi kami di:",
        ko: "질문이 있으시면 다음으로 연락 주세요:",
        vn: "Nếu bạn có bất kỳ câu hỏi nào, vui lòng liên hệ với chúng tôi tại:"
    },
    "Return to Home": {
        en: "Return to Home",
        id: "Kembali ke Beranda",
        ko: "홈으로 돌아가기",
        vn: "Quay Về Trang Chủ"
    },
    "We look forward to helping you on your Korean language journey.": {
        en: "We look forward to helping you on your Korean language journey.",
        id: "Kami menantikan untuk membantu Anda dalam perjalanan belajar bahasa Korea.",
        ko: "한국어 학습 여정에서 도움을 드릴 수 있기를 기대합니다.",
        vn: "Chúng tôi mong được giúp đỡ bạn trong hành trình học tiếng Hàn."
    },

    // Language selector labels
    "English": {
        en: "English",
        id: "English",
        ko: "English",
        vn: "English"
    },
    "Bahasa Indonesia": {
        en: "Bahasa Indonesia",
        id: "Bahasa Indonesia",
        ko: "Bahasa Indonesia",
        vn: "Bahasa Indonesia"
    },
    "Korean": {
        en: "Korean",
        id: "Korean",
        ko: "한국어",
        vn: "Tiếng Hàn"
    },
    "Vietnamese": {
        en: "Vietnamese",
        id: "Vietnamese",
        ko: "베트남어",
        vn: "Tiếng Việt"
    },

    // Additional form messages
    "Submitting...": {
        en: "Submitting...",
        id: "Mengirimkan...",
        ko: "제출 중...",
        vn: "Đang gửi..."
    },
    "Your survey has been submitted successfully!": {
        en: "Your survey has been submitted successfully!",
        id: "Survei Anda telah berhasil dikirim!",
        ko: "설문이 성공적으로 제출되었습니다!",
        vn: "Khảo sát của bạn đã được gửi thành công!"
    },
    "Failed to send form data. Please try again.": {
        en: "Failed to send form data. Please try again.",
        id: "Gagal mengirim data formulir. Silakan coba lagi.",
        ko: "양식 데이터 전송에 실패했습니다. 다시 시도해주세요.",
        vn: "Không thể gửi dữ liệu biểu mẫu. Vui lòng thử lại."
    },
    "There was a problem submitting your form. Please try again.": {
        en: "There was a problem submitting your form. Please try again.",
        id: "Ada masalah saat mengirimkan formulir Anda. Silakan coba lagi.",
        ko: "양식 제출에 문제가 발생했습니다. 다시 시도해주세요.",
        vn: "Có vấn đề khi gửi biểu mẫu của bạn. Vui lòng thử lại."
    },
    "An unexpected error occurred. Please try again.": {
        en: "An unexpected error occurred. Please try again.",
        id: "Terjadi kesalahan yang tidak terduga. Silakan coba lagi.",
        ko: "예기치 않은 오류가 발생했습니다. 다시 시도해주세요.",
        vn: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại."
    },
    "Please enter a valid email address": {
        en: "Please enter a valid email address",
        id: "Silakan masukkan alamat email yang valid",
        ko: "유효한 이메일 주소를 입력하세요",
        vn: "Vui lòng nhập địa chỉ email hợp lệ"
    },
    "Please select at least one learning goal": {
        en: "Please select at least one learning goal",
        id: "Silakan pilih setidaknya satu tujuan belajar",
        ko: "최소 하나의 학습 목표를 선택하세요",
        vn: "Vui lòng chọn ít nhất một mục tiêu học tập"
    },
    "Korean Learning Option": {
        en: "Korean Learning Option",
        id: "Opsi Pembelajaran Bahasa Korea",
        ko: "한국어 학습 옵션",
        vn: "Tùy Chọn Học Tiếng Hàn"
    },
    "Or enter your timezone (e.g. GMT+8, EST, etc.)": {
        en: "Or enter your timezone (e.g. GMT+8, EST, etc.)",
        id: "Atau masukkan zona waktu Anda (contoh: GMT+7, WIB, dll.)",
        ko: "또는 시간대를 입력하세요 (예: GMT+9, KST 등)",
        vn: "Hoặc nhập múi giờ của bạn (ví dụ: GMT+7, ICT, v.v.)"
    }
};

// Translation function to get text in the selected language
export const translate = (key: string, language: Language): string => {
    if (!translations[key]) {
        //console.warn(`Translation key not found: ${key}`);
        return key;
    }
    return translations[key][language] || key;
};